import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Input } from "../../components/ui/input";
import { PasswordInput } from "../../components/ui/password-input";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { login, isLoggedIn } from "../../lib/auth";
import { useBrand, brandLogo } from "../../lib/brand";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";

export default function AdminLogin() {
  const nav = useNavigate();
  const loc = useLocation();
  const brand = useBrand();
  const logoLight = brandLogo(brand, "light");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [mfaRequired, setMfaRequired] = useState(false);
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (isLoggedIn()) nav("/admin");
  }, []);

  async function submit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await login(email, password, otp || undefined);
      if (res.mfa_required) {
        setMfaRequired(true);
        toast.info("Insira o código do seu autenticador");
      } else if (res.token) {
        if (res.must_change_password) {
          toast.info("Troca de senha obrigatória no primeiro acesso");
          nav("/admin/trocar-senha");
        } else {
          toast.success("Acesso liberado");
          nav("/admin");
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha no login");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#22302E] text-white flex items-center justify-center px-6 font-admin" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          {logoLight ? (
            <img src={logoLight} alt="Azuhomes" className="h-14 mx-auto" />
          ) : (
            <div className="font-display text-4xl tracking-tight text-white">Azuhomes</div>
          )}
          <div className="text-xs uppercase tracking-[0.32em] text-[#3B9FD4] mt-4">Painel administrativo</div>
        </div>
        <form onSubmit={submit} className="bg-white text-[#22302E] rounded-md p-8 shadow-2xl" data-testid="admin-login-form">
          <div className="mb-6">
            <div className="flex items-center gap-2 text-[#0AA89C]">
              <ShieldCheck className="w-5 h-5" />
              <h1 className="text-xl font-semibold">Acesso restrito</h1>
            </div>
            <p className="text-sm text-[#22302E]/70 mt-1">Utilize as credenciais fornecidas pela equipe.</p>
          </div>
          <div className="space-y-4">
            <div><Label>E-mail</Label>
              <Input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" />
            </div>
            <div><Label htmlFor="login-password">Senha</Label>
              <PasswordInput id="login-password" data-testid="login-password" value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" />
            </div>
            {mfaRequired && (
              <div><Label>Código MFA (6 dígitos)</Label>
                <Input data-testid="login-otp" inputMode="numeric" maxLength={6} value={otp} onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, ""))} required />
              </div>
            )}
          </div>
          <Button data-testid="login-submit" type="submit" disabled={loading} className="w-full mt-6 bg-[#0AA89C] hover:bg-[#066B63] text-white">
            {loading ? "Autenticando..." : "Entrar"}
          </Button>
          <div className="mt-4 text-center">
            <Link to="/admin/esqueci-senha" className="text-xs text-[#0AA89C] hover:text-[#066B63] underline" data-testid="forgot-password-link">
              Esqueci minha senha
            </Link>
          </div>
          <div className="text-xs text-[#22302E]/60 mt-4 text-center">Ao entrar você concorda com os termos internos de uso.</div>
        </form>
      </div>
    </div>
  );
}
